import React, { lazy, useCallback, useMemo } from 'react';

import { connect } from 'react-redux';
import { Navigate, Route, RouteProps, Routes as RouterRoutes } from 'react-router';

import HeroWrapper from '@/components/layout/HeroWrapper';
import SignedInLayout from '@/components/layout/SignInLayout';
import SignedOutLayout from '@/components/layout/SignOutLayout';

import { UserDataState } from '@/components/AppInitializer/types';
import { AnonymousGuard, DefaultLayout, routeTree } from './config';
import { AppRouteObject, SharedRouteProps } from './types';

type RoutesProps = {
  status?: any;
  servicePartner?: any;
  userInfo?: any;
  userDataState?: UserDataState;
  userLoggedIn?: boolean;
};

const NotFound = lazy(() => import(/* webpackChunkName: "NotFound" */ '@/pages/NotFound'));

type ProRouteProps = RouteProps &
  SharedRouteProps & {
    isHidden: boolean | ((params: any) => boolean);
  } & any;

export function Routes({ userInfo, servicePartner, status, userDataState, userLoggedIn }: RoutesProps) {
  const [{ brandId: servicePartnerBrandId = null } = {}] = servicePartner?.brands || [];
  const SignedOutLayoutComponent = SignedOutLayout as React.ComponentType<{ isNotAuthPage?: boolean }>;
  const filterHidden = useCallback(
    (route: ProRouteProps) => {
      const isHidden = route.isHidden ?? false;
      const shouldBeHidden = typeof isHidden === 'function' ? isHidden({ brandId: servicePartnerBrandId }) : isHidden;
      return !shouldBeHidden;
    },
    [servicePartnerBrandId],
  );

  const filterRouteTree = useCallback(
    (routes: AppRouteObject[]): AppRouteObject[] => {
      return routes.reduce<AppRouteObject[]>((accumulator, route) => {
        if (!filterHidden(route as ProRouteProps)) {
          return accumulator;
        }

        const nextRoute = route.children?.length
          ? {
              ...route,
              children: filterRouteTree(route.children),
            }
          : route;

        accumulator.push(nextRoute);
        return accumulator;
      }, []);
    },
    [filterHidden],
  );

  const visibleRouteTree = useMemo(() => filterRouteTree(routeTree), [filterRouteTree]);

  const getLayoutElement = useCallback(
    (Layout: React.ComponentType<any>) => {
      if (Layout === SignedInLayout) {
        return <SignedInLayout status={status} servicePartner={servicePartner} userInfo={userInfo} />;
      }

      if (Layout === SignedOutLayout) {
        return <SignedOutLayoutComponent isNotAuthPage={false} />;
      }

      if (Layout === DefaultLayout) {
        return <DefaultLayout />;
      }

      return <Layout />;
    },
    [servicePartner, status, userInfo],
  );

  const renderRoute = useCallback(
    (route: AppRouteObject, keyPrefix = 'route') => {
      const {
        id,
        path,
        index,
        page: Page,
        guard: Guard = AnonymousGuard,
        heroConfig,
        layout: Layout,
        children,
        handle,
        ...routeProps
      } = route;

      const key = `${keyPrefix}-${id ?? path ?? 'group'}`;
      const childRoutes = children?.map((childRoute, childIndex) => renderRoute(childRoute, `${key}-${childIndex}`));

      if (Layout && !Page && children) {
        return (
          <Route key={key} element={getLayoutElement(Layout)} handle={handle}>
            {childRoutes}
          </Route>
        );
      }

      const element = Page ? (
        <Guard userInfo={userInfo} userDataState={userDataState} userLoggedIn={userLoggedIn} {...routeProps}>
          <HeroWrapper heroConfig={heroConfig}>
            <Page />
          </HeroWrapper>
        </Guard>
      ) : undefined;

      if (index) {
        return <Route key={key} index element={element} handle={handle} />;
      }

      return (
        <Route key={key} path={path} element={element} handle={handle}>
          {childRoutes}
        </Route>
      );
    },
    [getLayoutElement, userDataState, userInfo, userLoggedIn],
  );

  return (
    <RouterRoutes>
      {visibleRouteTree.map((route, index) => renderRoute(route, `root-${index}`))}

      {/* Home redirect: defer until user data is loaded to avoid early login redirect */}
      <Route
        path="/"
        element={
          userDataState === UserDataState.LOADED ? <Navigate to={userInfo ? '/systems' : '/login'} replace /> : <></>
        }
      />

      {/* 404 Fallback */}
      {userInfo ? (
        <Route element={<SignedInLayout status={status} servicePartner={servicePartner} userInfo={userInfo} />}>
          <Route path="*" element={<NotFound />} />
        </Route>
      ) : (
        <Route element={<DefaultLayout />}>
          <Route path="*" element={<NotFound />} />
        </Route>
      )}
    </RouterRoutes>
  );
}

export default connect(({ app: { userInfo, servicePartner, userDataState, userLoggedIn } }) => ({
  userInfo,
  servicePartner,
  userDataState,
  userLoggedIn,
}))(Routes);
